import { Typography } from "@mui/material";
import { useDeleteLicenseMutation } from "Api/Mutations/Licenses/useDeleteLicenseMutation";
import { PendingLicenseActions } from "Components/Licenses/PendingLicenseActions";
import { PendingLicensePaymentDialog } from "Components/Licenses/PendingLicensePaymentDialog";
import { PendingLicenseSelector } from "Components/Licenses/PendingLicenseSelector";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import type { PartyLicenseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { useState } from "react";
import { toast } from "sonner";

const PAGE_RESOURCES = Resources.Hunter.LicensePaymentPending;
const LICENSE_DETAIL_RESOURCES = Resources.Pages.Content.LicenseDetail;

type Props = {
  pendingLicenses: Array<PartyLicenseDto>;
  selectedLicense?: PartyLicenseDto;
  onSelectLicense: (licensePublicID: string) => void;
  onOrderAnotherLicense: () => void;
};

// The "payment in progress" half of the blocked screen: which order is
// selected, its payment details, and cancelling it — including those two
// flows' dialogs. Ordering stays with the page (shared with the no-license
// state).
export const PendingLicenseContent = ({
  pendingLicenses,
  selectedLicense,
  onSelectLicense,
  onOrderAnotherLicense,
}: Props) => {
  const { t } = useResource();
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const {
    isOpen: isCancelConfirmOpen,
    handleResult: handleCancelResult,
    open: openCancelConfirm,
  } = useConfirmDialog();
  const { mutate: deleteLicense, isPending: isCancelling } =
    useDeleteLicenseMutation();

  const handleCancelOrder = () => {
    if (!selectedLicense) {
      return;
    }
    deleteLicense(selectedLicense.publicID, {
      onError: () => toast.error(t(Resources.Errors.RequestError)),
    });
  };

  return (
    <>
      <Typography variant="body1" color="text.secondary">
        {t(PAGE_RESOURCES.ProcessingInfo)}
      </Typography>
      <PendingLicenseSelector
        licenses={pendingLicenses}
        selectedLicense={selectedLicense}
        onSelect={onSelectLicense}
      />
      <PendingLicenseActions
        onShowPaymentDetails={() => setIsPaymentDialogOpen(true)}
        onOrderAnotherLicense={onOrderAnotherLicense}
        onCancelOrder={openCancelConfirm}
        isCancelDisabled={isCancelling}
      />
      {selectedLicense && (
        <PendingLicensePaymentDialog
          isOpen={isPaymentDialogOpen}
          onClose={() => setIsPaymentDialogOpen(false)}
          license={selectedLicense}
        />
      )}
      <ConfirmDialog
        isDestructive
        isOpen={isCancelConfirmOpen}
        modalTitle={t(LICENSE_DETAIL_RESOURCES.CancelOrder)}
        modalContent={
          <Typography>
            {t(LICENSE_DETAIL_RESOURCES.CancelOrderConfirmation)}
          </Typography>
        }
        handleResult={handleCancelResult(handleCancelOrder)}
      />
    </>
  );
};
